import type { MicroUnit, OrderedMicroUnit } from '../types/segmentationTypes'

const sanitizeLabelPrefix = (raw: unknown): string | null => {
  if (raw === null || raw === undefined) return null
  let text = String(raw).trim()
  if (!text) return null

  text = text.replace(/[—–]/g, '-')
  let token = text.split(/\s+/)[0]
  if (token.includes('-')) {
    token = token.split('-')[0]
  } else if (token.includes('_')) {
    token = token.split('_')[0]
  } else if (token.includes('/')) {
    token = token.split('/')[0]
  }

  token = token.replace(/[^A-Za-z0-9]/g, '')
  if (!token) return null

  if (token === token.toLowerCase() || token === token.toUpperCase()) {
    token = token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
  }

  return token
}

const extractPrefixFromIdentifier = (identifier: unknown): string | null => {
  if (!identifier) return null
  let token = String(identifier).trim()
  if (!token) return null

  token = token.replace(/[—–]/g, '-').replace(/^[A-Z]+_/, '')
  let candidate = token
  if (token.includes('-')) {
    candidate = token.split('-')[0]
  } else if (token.includes('_')) {
    candidate = token.split('_')[0]
  }

  candidate = candidate.replace(/[^A-Za-z0-9]/g, '')
  if (!candidate || !/[A-Za-z]/.test(candidate)) return null

  if (candidate === candidate.toLowerCase() || candidate === candidate.toUpperCase()) {
    candidate = candidate.charAt(0).toUpperCase() + candidate.slice(1).toLowerCase()
  }

  return candidate
}

const inferLabelPrefix = (result: any): string => {
  const metadata = (result && typeof result === 'object' && result.segmentation_metadata && typeof result.segmentation_metadata === 'object')
    ? result.segmentation_metadata
    : {}

  const existing = sanitizeLabelPrefix(metadata?.unit_label_prefix)
  if (existing) {
    return existing
  }

  const candidateKeys = [
    'chapter_prefix',
    'chapter_code',
    'chapter_abbreviation',
    'chapter_sigla',
    'witness_prefix',
    'witness_sigla',
    'sigla'
  ]

  for (const key of candidateKeys) {
    const candidate = sanitizeLabelPrefix(metadata?.[key as keyof typeof metadata])
    if (candidate) {
      if (metadata && typeof metadata === 'object') {
        metadata.unit_label_prefix = candidate
      }
      return candidate
    }
  }

  const aggregated = (result && typeof result === 'object' && result.aggregated_statistics && typeof result.aggregated_statistics === 'object')
    ? result.aggregated_statistics
    : {}

  const aggregatedPrefix = sanitizeLabelPrefix(aggregated?.chapter_prefix)
  if (aggregatedPrefix) {
    if (metadata && typeof metadata === 'object') {
      metadata.unit_label_prefix = aggregatedPrefix
    }
    return aggregatedPrefix
  }

  const chunks: any[] = Array.isArray(result?.chunk_results) ? result.chunk_results : []
  for (const chunk of chunks) {
    const prefix = extractPrefixFromIdentifier(chunk?.source_chunk_id ?? chunk?.parent_primary_chunk)
    if (prefix) {
      if (metadata && typeof metadata === 'object') {
        metadata.unit_label_prefix = prefix
      }
      return prefix
    }
    const units: MicroUnit[] = Array.isArray(chunk?.micro_units) ? chunk.micro_units : []
    for (const unit of units) {
      const unitPrefix = extractPrefixFromIdentifier(unit?.unit_id)
      if (unitPrefix) {
        if (metadata && typeof metadata === 'object') {
          metadata.unit_label_prefix = unitPrefix
        }
        return unitPrefix
      }
    }
  }

  if (metadata && typeof metadata === 'object') {
    metadata.unit_label_prefix = 'Unit'
  }
  return 'Unit'
}

const assignSequentialUnitLabels = (result: any, chunks: any[]): string => {
  const prefix = inferLabelPrefix(result)

  const ordered = chunks.flatMap((chunk, chunkIndex) => {
    const units: MicroUnit[] = Array.isArray(chunk?.micro_units) ? chunk.micro_units : []
    return units.map((unit, unitIndex) => ({
      unit,
      key: [
        safeNumeric((unit as any)?.start_position),
        safeNumeric((unit as any)?.chunk_relative_start),
        chunkIndex,
        unitIndex
      ] as [number, number, number, number]
    }))
  })

  ordered.sort((a, b) => compareTuple(a.key, b.key))

  ordered.forEach((entry, globalIndex) => {
    const unit = entry.unit
    const label = `${prefix}.${globalIndex + 1}`
    unit.unit_label = label

    if (!unit.processing_metadata || typeof unit.processing_metadata !== 'object') {
      unit.processing_metadata = { unit_label: label }
    } else {
      unit.processing_metadata.unit_label = label
    }
    ; (unit.processing_metadata as Record<string, unknown>)['global_unit_index'] = globalIndex

    const metadataRecord = unit.processing_metadata as Record<string, unknown>
    const canonicalRowIdRaw =
      (metadataRecord && typeof metadataRecord.canonical_row_id === 'string'
        ? metadataRecord.canonical_row_id
        : undefined) ??
      ((unit as unknown as { canonical_row_id?: unknown }).canonical_row_id)

    if (typeof canonicalRowIdRaw === 'string' && canonicalRowIdRaw.length > 0) {
      metadataRecord['canonical_row_id'] = canonicalRowIdRaw
    }
  })

  if (result && typeof result === 'object' && result.segmentation_metadata && typeof result.segmentation_metadata === 'object') {
    result.segmentation_metadata.unit_label_prefix = prefix
  }

  return prefix
}

const rebuildNumbersMapping = (chunks: any[]): Record<string, Array<{ unit_uuid: string; unit_number: number; unit_id: string; unit_label?: string; canonical_row_id?: string }>> => {
  const numbers: Record<string, Array<{ unit_uuid: string; unit_number: number; unit_id: string; unit_label?: string; canonical_row_id?: string }>> = {}

  chunks.forEach((chunk, index) => {
    const units: MicroUnit[] = Array.isArray(chunk?.micro_units) ? chunk.micro_units : []
    if (!units.length) return

    const entries = units
      .filter(unit => typeof unit?.unit_uuid === 'string' && unit.unit_uuid.length > 0)
      .map((unit, unitIndex) => ({
        unit_uuid: unit.unit_uuid!,
        unit_number: typeof unit.unit_number === 'number' ? unit.unit_number : unitIndex + 1,
        unit_id: unit.unit_id,
        unit_label: unit.unit_label,
        canonical_row_id: unit.processing_metadata?.canonical_row_id
      }))

    if (entries.length) {
      const chunkKey = chunk?.source_chunk_id ?? chunk?.parent_primary_chunk ?? `chunk_${index}`
      numbers[chunkKey] = entries
    }
  })

  return numbers
}


const safeNumeric = (value: any): number => {
  if (value === null || value === undefined) return Number.POSITIVE_INFINITY
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
}

const extractNumericSuffix = (identifier: string | undefined | null): number | null => {
  if (!identifier) return null
  const match = identifier.match(/(\d+)(?!.*\d)/)
  if (!match) return null
  const value = Number(match[1])
  return Number.isFinite(value) ? value : null
}

const minFinite = (values: Array<number | null | undefined>): number | null => {
  let result: number | null = null
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      result = result === null ? value : Math.min(result, value)
    }
  }
  return result
}

const chunkOrderSortKey = (chunk: any, fallbackIndex: number): [number, number, number, number] => {
  const chunkId = chunk?.source_chunk_id ?? chunk?.parent_primary_chunk ?? ''
  const numericSuffix = extractNumericSuffix(chunkId)

  const units: MicroUnit[] = Array.isArray(chunk?.micro_units) ? (chunk.micro_units as MicroUnit[]) : []
  const minStart = minFinite(units.map(unit => unit?.start_position))
  const minChunkRelative = minFinite(units.map(unit => unit?.chunk_relative_start))

  const startKey = minStart ?? (typeof chunk?.chunk_position_data?.start_position === 'number'
    ? chunk.chunk_position_data.start_position
    : Number.POSITIVE_INFINITY)
  const relKey = minChunkRelative ?? (typeof chunk?.chunk_position_data?.relative_start === 'number'
    ? chunk.chunk_position_data.relative_start
    : Number.POSITIVE_INFINITY)
  const numericKey = numericSuffix ?? Number.POSITIVE_INFINITY

  return [startKey, relKey, numericKey, fallbackIndex]
}

const compareTuple = (a: [number, number, number, number], b: [number, number, number, number]): number => {
  for (let i = 0; i < a.length; i += 1) {
    const valA = a[i]
    const valB = b[i]
    const finiteA = Number.isFinite(valA)
    const finiteB = Number.isFinite(valB)
    if (finiteA && finiteB) {
      const delta = valA - valB
      if (delta !== 0) {
        return delta
      }
    } else if (finiteA && !finiteB) {
      return -1
    } else if (!finiteA && finiteB) {
      return 1
    }
  }
  return 0
}

const normalizeMicroUnits = (chunk: any): MicroUnit[] => {
  if (!Array.isArray(chunk?.micro_units)) {
    return []
  }

  const decorated = (chunk.micro_units as MicroUnit[])
    .map((unit, index) => ({ unit, originalIndex: index }))
    .sort((a: OrderedMicroUnit, b: OrderedMicroUnit) => {
      // CRITICAL: start_position is authoritative; chunk_relative_start can become stale after edits
      const keyA = [
        safeNumeric(a.unit?.start_position),
        safeNumeric(a.unit?.chunk_relative_start),
        safeNumeric(a.unit?.unit_number),
        a.originalIndex
      ]
      const keyB = [
        safeNumeric(b.unit?.start_position),
        safeNumeric(b.unit?.chunk_relative_start),
        safeNumeric(b.unit?.unit_number),
        b.originalIndex
      ]

      for (let i = 0; i < keyA.length; i += 1) {
        const delta = keyA[i] - keyB[i]
        if (delta !== 0) {
          return delta
        }
      }
      return 0
    })

  return decorated.map((entry: OrderedMicroUnit, sortedIndex: number) => {
    const unit = entry.unit
    const normalizedIndex = sortedIndex + 1
    const originalMetadata = unit.processing_metadata ?? {}
    const normalizedMetadata = {
      ...originalMetadata,
      original_index: entry.originalIndex,
      normalized_sequence_index: sortedIndex,
      original_unit_number: originalMetadata.original_unit_number ?? unit.unit_number ?? null,
      original_unit_id: originalMetadata.original_unit_id ?? unit.unit_id ?? null
    }

    const normalizedUnit: MicroUnit = {
      ...unit,
      unit_number: normalizedIndex,
      processing_metadata: normalizedMetadata
    }

    if (typeof unit.unit_id === 'string' && /_U\d+$/.test(unit.unit_id)) {
      const idPrefix = unit.unit_id.replace(/_U\d+$/, '')
      normalizedUnit.unit_id = `${idPrefix}_U${normalizedIndex}`
    }

    return normalizedUnit
  })
}

export const normalizeSegmentationResult = <T extends { chunk_results?: any[] } | null | undefined>(result: T): T => {
  if (!result || !Array.isArray(result.chunk_results)) {
    return result
  }

  const normalizedChunks: Array<{ chunk: any; key: [number, number, number, number] }> = result.chunk_results.map((chunk: any, index: number) => {
    const normalizedMicroUnits = normalizeMicroUnits(chunk)
    const normalizedChunk = Array.isArray(chunk?.micro_units)
      ? { ...chunk, micro_units: normalizedMicroUnits }
      : chunk
    return {
      chunk: normalizedChunk,
      key: chunkOrderSortKey(normalizedChunk, index)
    }
  })

  const sortedChunks = normalizedChunks
    .slice()
    .sort((a, b) => compareTuple(a.key, b.key))
    .map(entry => entry.chunk)

  const prefix = assignSequentialUnitLabels(result, sortedChunks)
  const numbers = rebuildNumbersMapping(sortedChunks)

  const normalizedResult: any = {
    ...result,
    chunk_results: sortedChunks,
    numbers
  }

  if (normalizedResult.segmentation_metadata && typeof normalizedResult.segmentation_metadata === 'object') {
    normalizedResult.segmentation_metadata.unit_label_prefix = prefix
  }

  return normalizedResult
}

/**
 * Ensure each chunk has sequential unit_number/unit_id ordering consistent with text order.
 * Returns a normalized clone; safe to call before entering edit mode.
 */
export const ensureSequentialSegmentationResult = <T extends { chunk_results?: any[] } | null | undefined>(result: T): T => {
  return normalizeSegmentationResult(result)
}
